import styled, { css } from 'styled-components';
import {
  CpBody03Placeholder,
  CpHeading03Primary,
} from 'components/atoms/customer-portal/typography';
import { Color } from 'components/colors';
import { SearchInput } from '../components/cp-input';
import { CpWidgetHeader } from './common/header';

export const FilterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  flex-direction: column;
  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

export enum PaddingSize {
  None = 0,
  Small = 2,
  Medium = 3,
  Large = 5,
}

export const getPadding = (n: PaddingSize | undefined) => {
  switch (n) {
    case PaddingSize.None:
      return '0';
    case PaddingSize.Small:
    default:
      return 'var(--cp-spacing-11)';

    case PaddingSize.Medium:
      return 'var(--cp-spacing-12)';
    case PaddingSize.Large:
      return 'var(--cp-spacing-13)';
  }
};

// hex to highly sensitive poo
const hexToHsp = (H: string) => {
  // Convert hex to RGB first
  let r = 0;
  let g = 0;
  let b = 0;

  if (H.length === 4) {
    r = parseInt(H[1] + H[1], 16);
    g = parseInt(H[2] + H[2], 16);
    b = parseInt(H[3] + H[3], 16);
  } else if (H.length === 7) {
    r = parseInt(H[1] + H[2], 16);
    g = parseInt(H[3] + H[4], 16);
    b = parseInt(H[5] + H[6], 16);
  }
  // Then to HSP
  const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));

  return hsp;
};

export const isBackgroundLight = (background: string) => {
  const hsp = hexToHsp(background);

  return hsp > 127.5;
};

export const isBackgroundVeryLight = (background: string) => {
  const hsp = hexToHsp(background);

  return hsp > 230;
};

export const CpSectionWrapper = styled.div.attrs<{
  className?: string;
  isDisabled?: boolean;
  background?: string;
  isBackgroundImage?: boolean;
  noHorizontalPadding?: boolean;
  noVerticalPadding?: boolean;
}>((props) => {
  const bg = props.background;
  const isLight = bg ? isBackgroundLight(bg) : true;
  const isVeryLight = bg ? isBackgroundVeryLight(bg) : true;

  const classnames = ['rl-cp-section'];

  if (isVeryLight) {
    classnames.push('cp-very-light-background');
  } else if (isLight) {
    classnames.push('cp-light-background');
  } else {
    classnames.push('cp-dark-background');
  }

  return {
    className: classnames.join(' '),
  };
})<{
  paddingY?: PaddingSize;
  isDisabled?: boolean;
  background?: string;
  noHorizontalPadding?: boolean;
  noVerticalPadding?: boolean;
  isBackgroundImage?: boolean;
}>`
  position: relative;
  width: 100%;
  max-width: 100%;
  overflow-y: auto;
  margin-left: auto;
  margin-right: auto;
  pointer-events: ${(props) => (props.isDisabled ? 'none' : 'auto')};
  ${(props) =>
    props.noHorizontalPadding
      ? css``
      : css`
          padding-left: max(var(--spacing-10), calc(calc(100% - 1080px) / 2));
          padding-right: max(var(--spacing-10), calc(calc(100% - 1080px) / 2));
        `};
  ${(props) => {
    const padding = props?.paddingY
      ? getPadding(props?.paddingY)
      : 'var(--cp-spacing-11)';

    return props.noVerticalPadding
      ? css``
      : css`
          padding-top: ${padding};
          padding-bottom: ${padding};

          @media (max-width: 780px) {
            padding-top: calc(${padding} / 1.5);
            padding-bottom: calc(${padding} / 1.5);
          }

          @media (max-width: 480px) {
            padding-top: calc(${padding} / 2);
            padding-bottom: calc(${padding} / 2);
          }
        `;
  }};

  background: ${(props) => props.background};
  background-position: center;
  background-size: cover;
  /* background-attachment: fixed; */
  background-repeat: no-repeat;
  box-shadow: ${(props) =>
    props.isBackgroundImage ? 'inset 0 0 0 2000px rgba(0, 0, 0, 0.3)' : 'none'};
  .ant-skeleton.ant-skeleton-active .ant-skeleton-button {
    background: linear-gradient(
      90deg,
      var(--cp-black-opacity-20-hover) 25%,
      var(--cp-black-opacity-30-hover) 37%,
      var(--cp-black-opacity-20-hover) 63%
    );
    background-size: 400% 100%;
  }
  &.cp-dark-background .ant-skeleton.ant-skeleton-active .ant-skeleton-button {
    background: linear-gradient(
      90deg,
      var(--cp-white-opacity-20-hover) 25%,
      var(--cp-white-opacity-30-hover) 37%,
      var(--cp-white-opacity-20-hover) 63%
    );
    background-size: 400% 100%;
  }

  &.cp-dark-background .cp-auto-adjust-color {
    --cp-text-primary: var(--cp-white);
    --cp-text-secondary: var(--cp-white-80);
    --cp-text-placeholder: var(--cp-white);
  }

  &.cp-dark-background {
    --cp-border-subtle-auto-adjust: var(--cp-border-subtle-inverse);
    --cp-border-strong-auto-adjust: var(--cp-border-strong-inverse);
  }

  &.cp-dark-background {
    svg.cp-svg-auto-adjust-fill {
      fill: var(--cp-white);
    }
    .swiper-pagination-bullet {
      background-color: gray;
    }
    .swiper-pagination-bullet-active {
      background-color: white;
    }
    .controlButton,
    .controlButtonMinimized {
      border-color: white;
    }
    .cp-footer {
      border: yellow solid 2px;
    }
  }

  ${(props) =>
    props.noHorizontalPadding
      ? css``
      : css`
          @media (max-width: 480px) {
            padding-left: var(--cp-spacing-05);
            padding-right: var(--cp-spacing-05);
          }

          /* padding left and right to var(--cp-spacing-06) in tab mode where min width is max width is 768px and min width is 481px */
          @media (min-width: 481px) and (max-width: 768px) {
            padding-left: var(--cp-spacing-06);
            padding-right: var(--cp-spacing-06);
          }
        `};

  :not(.rl-cp-scale-wrapper) &:first-child {
    min-height: calc(100vh - 52px);
  }
  ${CpWidgetHeader}, ${FilterBar}, ${SearchInput} {
    margin-bottom: var(--cp-spacing-07);
  }
  @media (max-width: 480px) {
    ${CpWidgetHeader}, ${FilterBar}, ${SearchInput} {
      margin-bottom: var(--cp-spacing-06);
    }
  }
  @media (max-width: 768px) {
    ${CpWidgetHeader}, ${FilterBar}, ${SearchInput} {
      margin-bottom: var(--cp-spacing-05);
    }
  }
`;
export const CpWidgetTitle = styled(CpHeading03Primary)`
  margin-bottom: var(--cp-spacing-03);
`;

export const CpWidgetDescription = styled(CpBody03Placeholder)`
  margin-bottom: var(--cp-spacing-05);
`;

export const BgWrapper = styled.div<{
  paddingY?: PaddingSize | undefined;
}>`
  width: 100%;

  margin-left: auto;
  margin-right: auto;
  height: 80vh;
  overflow-y: hidden;
  border-radius: var(--border-radius-default);
  background: #ffffff7a;
  height: calc(100% - 106px);
  max-height: calc(100% - 106px);

  /* full height at 480px */
  @media (max-width: 480px) {
    height: calc(100% - 102px);
    max-height: calc(100% - 102px);
  }

  @media (min-width: 481px) and (max-width: 768px) {
    height: calc(100% - 106px);
    max-height: calc(100% - 106px);
  }
`;

export const ChevronWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 100px;
  border: 2px solid black;
  padding: var(--cp-spacing-01) var(--cp-spacing-03);
  margin-left: var(--cp-spacing-03);
  width: 48px;
  height: 32px;
  svg {
    fill: black;
  }
  :hover {
    background: var(--cp-background-as-black-opacity-02);
  }
  :active {
    background: var(--cp-background-as-black-opacity);
  }
`;

export const MarkasInProgress = styled.div`
  cursor: pointer;
  padding: 0.4rem var(--cp-spacing-05);
  border-radius: 100px;
  background: black;
  color: white;
  height: 32px;
  font-size: 0.75rem;

  :hover {
    background: var(--cp-gray-80);
  }

  :active {
    background: var(--cp-gray-90);
  }

  width: max-content;
  .status-label {
    font-size: 0.875rem;
    width: fit-content;
    display: block;
  }
`;

export const CompletedTag = styled.div<{ color?: Color }>`
  cursor: pointer;
  width: max-content;
  padding: 0.4rem var(--cp-spacing-05);
  border-radius: 100px;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => `var(--${props.color ?? 'green'}-50)`};
  font-size: 0.75rem;

  :hover {
    background: ${(props) => `var(--${props.color ?? 'green'}-60)`};
  }

  :active {
    background: ${(props) => `var(--${props.color ?? 'green'}-70)`};
  }

  svg {
    fill: white;
    margin-left: var(--cp-spacing-03);
  }
  .status-label {
    font-size: 0.875rem;
    width: max-content;
  }
`;

export const FullWidthCompletedTag = styled.div<{ color?: Color }>`
  cursor: pointer;
  padding: var(--cp-spacing-05);
  border-radius: 100px;
  color: white;
  display: flex;
  height: 56px;

  align-items: center;
  justify-content: center;
  background: ${(props) => `var(--${props.color ?? 'green'}-50)`};
  font-size: var(--cp-body-01-font-size);

  :hover {
    background: ${(props) => `var(--${props.color ?? 'green'}-60)`};
  }

  :active {
    background: ${(props) => `var(--${props.color ?? 'green'}-70)`};
  }

  svg {
    fill: white;
    margin-right: var(--cp-spacing-03);
  }

  .status-label {
    font-size: var(--cp-body-01-font-size);
    width: fit-content;
    display: block;
  }

  @media (max-width: 768px) {
    margin-bottom: var(--cp-spacing-07);
  }
`;

export const FullWidthMarkAsStatus = styled.div`
  cursor: pointer;
  padding: var(--cp-spacing-05);
  border-radius: 100px;
  background: black;
  color: white;
  height: 56px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--cp-body-01-font-size);

  :hover {
    background: var(--cp-gray-80);
  }

  :active {
    background: var(--cp-gray-90);
  }

  .status-label {
    width: fit-content;
    display: block;
  }

  @media (max-width: 768px) {
    margin-bottom: var(--cp-spacing-07);
  }
`;
